"use client";

import { useState, useRef, useEffect, useCallback } from "react";

// ============================================
// Chatbot — floating chat panel
// ============================================

interface ChatMessage {
  role: "user" | "bot";
  html: string;
}

interface ChatbotProps {
  chatResponses: Record<string, string>;
}

const WELCOME_MESSAGE =
  'What would you like to know? Try "Which members are based in Miami?" or "Summarize Austin before my call."';

function escapeHTML(str: string): string {
  const el = document.createElement("div");
  el.textContent = str;
  return el.innerHTML;
}

function matchResponse(
  msg: string,
  responses: Record<string, string>,
): string {
  const lower = msg.toLowerCase();

  if (lower.includes("churn") && lower.includes("miami")) {
    return responses["churn risk miami"] ?? responses["default"] ?? "";
  }
  if (lower.includes("summarize") && lower.includes("austin")) {
    return responses["summarize austin"] ?? responses["default"] ?? "";
  }
  if (lower.includes("summarize") && lower.includes("jeffrey")) {
    return responses["summarize jeffrey"] ?? responses["default"] ?? "";
  }
  if (lower.includes("dallas")) {
    return responses["who dallas"] ?? responses["default"] ?? "";
  }
  if (
    lower.includes("new member") ||
    lower.includes("joined") ||
    lower.includes("new this month")
  ) {
    return responses["new members"] ?? responses["default"] ?? "";
  }
  return responses["default"] ?? "";
}

export default function Chatbot({ chatResponses }: ChatbotProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: "bot", html: WELCOME_MESSAGE },
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom whenever messages change or typing state changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSend = useCallback(() => {
    const msg = inputValue.trim();
    if (!msg) return;

    // Add user message
    setMessages((prev) => [...prev, { role: "user", html: escapeHTML(msg) }]);
    setInputValue("");
    setIsTyping(true);

    // Simulate response after delay
    const delay = 800 + Math.random() * 600;
    setTimeout(() => {
      setIsTyping(false);
      const response = matchResponse(msg, chatResponses);
      setMessages((prev) => [...prev, { role: "bot", html: response }]);
    }, delay);
  }, [inputValue, chatResponses]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleSend();
    },
    [handleSend],
  );

  return (
    <>
      {/* Floating trigger button */}
      <button
        className="chatbot-trigger"
        onClick={handleOpen}
        style={{ display: isOpen ? "none" : "flex" }}
      >
        <svg viewBox="0 0 24 24">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
        </svg>
      </button>

      {/* Chat panel */}
      <div className={`chatbot-panel${isOpen ? " open" : ""}`}>
        <div className="chatbot-header">
          <span className="chatbot-title">Ask about your community</span>
          <button className="chatbot-close" onClick={handleClose}>
            &times;
          </button>
        </div>

        <div className="chatbot-messages">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`chat-msg ${m.role === "user" ? "user" : "bot"}`}
              dangerouslySetInnerHTML={{ __html: m.html }}
            />
          ))}
          {isTyping && (
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chatbot-input-row">
          <input
            ref={inputRef}
            type="text"
            className="chatbot-input"
            placeholder="Ask anything about members..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button className="chatbot-send" onClick={handleSend}>
            <svg viewBox="0 0 24 24">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}
